import { BusinessCategory } from './../schemas/business_category';
import { PaymentProvider } from './../schemas/payment_provider';
import { inject, Injectable, signal } from '@angular/core';
import { readItems, createItem, updateItem, deleteItem } from '@directus/sdk';
import { directusAuthInstance } from '../libs/directus';
import { Business } from '../schemas/business';
import { BusinessAddress } from '../schemas/business_address';
import { Preference } from '../schemas/user_preference';
import { HandleErrorsService } from '../utils/services/handle-errors.service';
import { RequestsService } from '../utils/services/requests.service';
import { TranslocoService } from '@jsverse/transloco';
import { UserType } from '../schemas/user_type';
import { Locale } from '../schemas/locale';
import { PaymentAccount } from '../schemas/payment_account';
import { user } from '@angular/fire/auth';
import { UserService } from './user.service';
import { BusinessContact } from '../schemas/business_contact';

@Injectable({
  providedIn: 'root',
})
export class BusinessService {
  private handleErrorsService = inject(HandleErrorsService);
  private requestsService = inject(RequestsService);
  private userService = inject(UserService);
  private transloco = inject(TranslocoService);
  directusClient = directusAuthInstance;

  // public businesses = signal<Business[] | null>(null);
  public paymentProviders = signal<PaymentProvider[]>([]);
  public businessCategories = signal<BusinessCategory[]>([]);
  public businessCategory = signal<BusinessCategory | null>(null);
  public business = signal<Business | null>(null);
  public businessPaymentAccounts = signal<PaymentAccount[]>([]);
  public businessDataLoaded = signal<boolean>(false);
  public loading = signal<boolean>(false);
  public address = signal<BusinessAddress | null>(null);
  public contacts = signal<BusinessContact[]>([]);
  public preferences = signal<Preference | null>(null);
  public language = this.transloco.getActiveLang();

  constructor() {}

  async init() {
    this.loading.set(true);
    const loginState = localStorage.getItem('isLoggedIn') === 'true';
    if (loginState) {
      const business = await this.getUserBusinesses();
      await this.getBusinessPaymentAccounts(false);
      this.loading.set(false);
      this.businessDataLoaded.set(true);
      return business;
    } else {
      this.loading.set(false);
      return null;
    }
  }

  public async getUserBusinesses() {
    if (!this.loading()) this.loading.set(true);
    const language = this.transloco.getActiveLang();
    try {
      const businesses = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          readItems('business', {
            fields: [
              '*',
              { logo: ['id', 'title', 'filename_disk', 'filename_disk'] },
              { banner: ['id', 'title', 'filename_disk', 'filename_disk'] },
              {
                country: [
                  'id',
                  'code',
                  'name',
                  'phone_code',
                  'currency',
                  'starts_with',
                ],
              },
              {
                categories: [
                  'id',
                  { category_id: ['*', { translations: ['*'] }] },
                ],
              },
              {
                business_addresses: ['id', { city: ['*'] }, 'street_address'],
              },
              {
                business_contacts: ['*'],
              },
            ],
            deep: {
              categories: {
                category_id: {
                  translations: {
                    _filter: {
                      _and: [
                        {
                          languages_code: { _eq: language },
                        },
                      ],
                    },
                  },
                },
              },
            },
            sort: 'date_created',
            limit: 1,
          }),
        ),
      );

      if (businesses.length === 0) {
        const businessData = await this.prepareBusinessData(null);
        this.loading.set(false);
        return businessData;
      }

      const businessData = await this.prepareBusinessData(businesses || null);
      this.loading.set(false);
      return businessData;
    } catch (error) {
      this.loading.set(false);
      this.handleErrorsService.handleErrors(error);
      return await this.prepareBusinessData(null);
    }
  }

  async prepareBusinessData(businesses: any[] | null) {
    if (businesses === null) return;

    const businessesData: Business[] = [];

    businesses.forEach((business: Business) => {
      const businessData: Business = {
        id: business.id,
        name: business.name,
        store_name: business.store_name,
        logo: business.logo,
        banner: business.banner,
        code: business.code,
        caption: business.caption,
        description: business.description,
        country: business.country,
        wifi_name: business.wifi_name,
        wifi_password: business.wifi_password,
      };

      const business_contacts: any[] = business?.business_contacts || [];
      const business_addresses: any[] = business?.business_addresses || [];
      const categories: any[] = business?.categories || [];
      const category = categories[0]?.category_id || null;

      if (business_contacts.length > 0) {
        this.contacts.set(business_contacts);
      }

      if (categories.length > 0 && category) {
        const categoryData: BusinessCategory = {
          id: category.id,
          business_category_id: categories[0].id,
          name: category.translations![0]?.name || category.name,
        };

        this.businessCategory.set(categoryData);
        businessData.category = categoryData || null;
      }

      if (business_addresses.length > 0) {
        this.address.set(business_addresses[0] as BusinessAddress);
      }

      businessesData.push(businessData);
    });

    // this.businesses.set(businessesData);
    this.business.set(businessesData[0]);
    // console.log('Business', this.address());
    return businessesData[0];
  }

  async createBusiness(business: Business) {
    this.loading.set(true);
    try {
      const businessResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(createItem('business', business)),
      );
      await this.getUserBusinesses();
      await this.userService.getUser();
      this.loading.set(false);
      return businessResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async updateBusiness(business: Business) {
    this.loading.set(true);

    const { id, ...data } = business;
    try {
      const businessResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          updateItem('business', this.business()!.id!, data),
          // updateItem('business', this.businesses()![0].id!, business),
        ),
      );
      this.loading.set(false);
      return businessResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async saveBusinessAddress(address: BusinessAddress) {
    this.loading.set(true);
    try {
      const businessResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(createItem('business_addresses', address)),
      );

      this.loading.set(false);
      return businessResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async saveBusinessContact(contact: BusinessContact) {
    this.loading.set(true);
    try {
      const businessResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(createItem('business_contacts', contact)),
      );

      this.loading.set(false);
      return businessResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async saveBusinessCategory(category: BusinessCategory) {
    this.loading.set(true);
    try {
      const businessResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          createItem('businesses_categories', category),
        ),
      );

      this.loading.set(false);
      return businessResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async updateBusinessAddress(address: BusinessAddress) {
    this.loading.set(true);
    const { id, ...data } = address;
    try {
      const businessResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          updateItem('business_addresses', address.id!, {
            ...data,
          }),
        ),
      );

      this.loading.set(false);
      return businessResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }
  async updateBusinessContact(contact: BusinessContact) {
    this.loading.set(true);
    const { id, ...data } = contact;

    // console.log(contact);
    try {
      const businessResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          updateItem('business_contacts', contact.id!, {
            ...data,
          }),
        ),
      );

      this.loading.set(false);
      return businessResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async updateBusinessCategory(category: BusinessCategory) {
    this.loading.set(true);
    try {
      const businessResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          updateItem(
            'businesses_categories',
            this.businessCategory()?.business_category_id!,
            {
              business_id: this.business()?.id,
              category_id: category.category_id,
            },
          ),
        ),
      );

      this.loading.set(false);
      return businessResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async getAllCategories() {
    if (this.businessCategories()?.length) return this.businessCategories();
    const language = this.transloco.getActiveLang();
    this.loading.set(true);
    // console.log('BossAuthTokens', this.bossTokenService.get());
    try {
      const categories: BusinessCategory[] = [];
      const categoriesResponse: BusinessCategory[] =
        await this.requestsService.handleRequest(() =>
          this.directusClient.request(
            readItems('business_category', {
              filter: {
                is_active: {
                  _eq: true,
                },
              },
              sort: ['sort', 'name'],
              limit: -1,
              deep: {
                translations: {
                  _filter: {
                    _and: [
                      {
                        languages_code: { _eq: language },
                      },
                    ],
                  },
                },
              },
              fields: ['id', 'name', { translations: ['*'] }],
            }),
          ),
        );

      categoriesResponse.forEach((category) => {
        const categoryData: BusinessCategory = {
          id: category.id,
          name: category.translations![0]?.name || category.name,
        };

        categories.push(categoryData);
      });

      this.businessCategories.set(categories);

      this.loading.set(false);
      return categories || [];
    } catch (error) {
      this.loading.set(false);
      this.handleErrorsService.handleErrors(error);
      return null;
    }
  }

  async getReferrers() {
    const language = this.transloco.getActiveLang();
    this.loading.set(true);
    // console.log('BossAuthTokens', this.bossTokenService.get());
    try {
      const referrers: any[] = [];
      const referrersResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          readItems('online_referrers', {
            deep: {
              translations: {
                _filter: {
                  _and: [
                    {
                      languages_code: { _eq: language },
                    },
                  ],
                },
              },
            },
            fields: ['id', 'name', { translations: ['*'] }],
          }),
        ),
      );

      referrersResponse.forEach((referrer: any) => {
        const referrerData: BusinessCategory = {
          id: referrer.id,
          name: referrer.translations[0]?.name || referrer.name,
        };

        referrers.push(referrerData);
      });

      this.loading.set(false);
      return referrers;
    } catch (error) {
      console.log('Referrers Error');
      this.loading.set(false);
      this.handleErrorsService.handleErrors(error);
      return null;
    }
  }

  async setPreferences(preference: Preference) {
    // const defaultLanguage = this.languageService.getAppLanguage();
    // const defaultTheme = this.themeService.getCurrentTheme();
    // let data: Preference | null = null;
    // if (preference) {
    //   this.languageService.setAppLanguage(preference.language as Language);
    //   this.themeService.changeTheme(preference.theme!);
    // }
    // data = {
    //   theme: preference?.theme || defaultTheme,
    //   language: (preference?.language as Language)?.id || defaultLanguage?.id,
    // };
    // console.log('Preferences', data);
    // // Save User Preferences
    // if (this.user() === null) return;
    // return await this.requestsService.handleRequest(() =>
    //   this.directusClient.request(createItem('user_preference', data!)),
    // );
  }

  async updatePreferences(preference: Preference) {
    // const defaultLanguage = this.languageService.getAppLanguage();
    // const defaultTheme = this.themeService.getCurrentTheme();
    // this.languageService.setAppLanguage(preference.language as Language);
    // this.themeService.changeTheme(preference.theme!);
    // const preferenceUpdate = { ...preference, id: this.preferences()!.id! };
    // this.preferences.set(preferenceUpdate);
    // const data = {
    //   theme: preference?.theme || defaultTheme,
    //   language: (preference?.language as Language)?.id || defaultLanguage?.id,
    // };
    // console.log('Preferences', data);
    // console.log('Other Preferences', this.preferences());
    // if (
    //   data.language === (this.preferences()!.language as Language)?.id &&
    //   data.theme === this.preferences()!.theme
    // )
    //   return;
    // console.log('Saving Update');
    // // Save User Preferences
    // if (this.user() === null) return;
    // return await this.requestsService.handleRequest(() =>
    //   this.directusClient.request(
    //     updateItem('user_preference', this.preferences()!.id!, data),
    //   ),
    // );
  }

  async getPaymentProviders(getCache: boolean = true) {
    if (this.paymentProviders()?.length > 0 && getCache) {
      return this.paymentProviders();
    }
    const language = this.transloco.getActiveLang();
    // this.loading.set(true);
    // console.log('Payment Providers', this.paymentProviders());
    // console.log('Business', this.business());
    // await new Promise((resolve) => setTimeout(resolve, 3000));
    // TODO: singe I have this data, I can decide to change the User Experience later to have two steps one for choosing the Account type that is aggregated from the response the show providers based on the account type or just show all the providers
    try {
      const paymentProviders: PaymentProvider[] = [];
      const paymentProviderResponse: PaymentProvider[] =
        await this.requestsService.handleRequest(() =>
          this.directusClient.request(
            readItems('payment_providers', {
              filter: {
                _and: [
                  {
                    is_active: {
                      _eq: true,
                    },
                  },
                  {
                    user_type: {
                      code: {
                        _eq: UserType.BUSINESS,
                      },
                    },
                  },
                  {
                    country: {
                      code: {
                        _eq: (this.business()!.country as Locale).code,
                      },
                    },
                  },
                ],
              },
              sort: 'sort',
              limit: -1,
              deep: {
                translations: {
                  _filter: {
                    _and: [
                      {
                        languages_code: { _eq: language },
                      },
                    ],
                  },
                },
              },
              // fields: ['id', 'name', { translations: ['*'] }],
              fields: [
                'id',
                'name',
                'code',
                'display_name',
                'icon',
                { account_type: ['*'] },
                { translations: ['*'] },
              ],
            }),
          ),
        );

      paymentProviderResponse.forEach((provider) => {
        const providerData: PaymentProvider = {
          id: provider.id,
          name: provider.translations![0]?.name || provider.name,
          display_name:
            provider.translations![0]?.display_name || provider.display_name,
          code: provider.code,
          icon: provider.icon,
          account_type: provider.account_type,
        };

        paymentProviders.push(providerData);
      });

      this.paymentProviders.set(paymentProviders);

      // this.loading.set(false);
      return paymentProviders || [];
    } catch (error) {
      // this.loading.set(false);
      this.handleErrorsService.handleErrors(error);
      return [];
    }
  }

  async getBusinessPaymentAccounts(getCache: boolean = true) {
    // console.trace('Getting Business Payment Methods');

    if (this.businessPaymentAccounts().length > 0 && getCache) {
      return this.businessPaymentAccounts();
    }

    // await new Promise((resolve) => setTimeout(resolve, 3000));

    const language = this.transloco.getActiveLang();
    this.loading.set(true);
    try {
      const accounts: PaymentAccount[] = [];
      const accountsResponse: PaymentAccount[] =
        await this.requestsService.handleRequest(() =>
          this.directusClient.request(
            readItems('payment_accounts', {
              filter: {
                business: {
                  _eq: this.business()!.id,
                },
                is_active: {
                  _eq: true,
                },
                // user: {
                //   _eq: '9b99eab7-9dfc-4928-97d3-da8ef22e26dc',
                // },
              },
              deep: {
                payment_provider: {
                  translations: {
                    _filter: {
                      _and: [
                        {
                          languages_code: { _eq: language },
                        },
                      ],
                    },
                  },
                },
                // business: {
                //   _filter: {
                //     _and: [
                //       {
                //         code: {
                //           _eq: this.business()!.code,
                //         },
                //       },
                //     ],
                //   },
                // },
              },
              fields: [
                'id',
                'name',
                'number',
                'number_display',
                'code',
                'label',
                {
                  type: ['code'],
                },
                // 'user.id',
                // 'user.names',
                // 'user.code',
                // 'business.id',
                // 'business.code',
                // 'business.name',
                {
                  payment_provider: [
                    'id',
                    'code',
                    'icon',
                    'name',
                    { translations: ['*'] },
                  ],
                },
                { country: ['id', 'name', 'code', 'phone_code', 'currency'] },
                { business: ['id', 'name', 'code'] },
                { user: ['id', 'names', 'code'] },
              ],
            }),
          ),
        );

      accountsResponse.forEach((account) => {
        const providerData: PaymentProvider = {
          id: account.payment_provider?.id || '',
          name:
            account.payment_provider?.translations![0]?.name ||
            account.payment_provider?.name,
          display_name:
            account.payment_provider?.translations![0]?.display_name ||
            account.payment_provider?.display_name,
          code: account.payment_provider?.code,
          icon: account.payment_provider?.icon,
        };
        const accountData: PaymentAccount = {
          id: account.id,
          name: account.name,
          number: account.number,
          number_display: account.number_display,
          label: account.label,
          type: account.type,
          code: account.code,
          payment_provider: providerData,
          country: account.country,
        };

        accounts.push(accountData);
      });

      this.businessPaymentAccounts.set(accounts);

      this.loading.set(false);
      return accounts || [];
    } catch (error) {
      this.loading.set(false);
      this.handleErrorsService.handleErrors(error);
      return [];
    }
  }

  async createBusinessPaymentAccount(account: PaymentAccount) {
    this.loading.set(true);

    const data = {
      name: account.name,
      number: account.number,
      number_display: account.number_display,
      label: account.label,
      type: account.type?.id,
      payment_provider: account.payment_provider?.id,
      country: account.country,
      is_business_account: true,
      business: this.business()!.id,
    };

    try {
      const accountResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(createItem('payment_accounts', data)),
      );

      await this.getBusinessPaymentAccounts(false);
      this.loading.set(false);
      return accountResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async updateBusinessPaymentAccount(account: PaymentAccount) {
    this.loading.set(true);

    const data = {
      name: account.name,
      number: account.number,
      number_display: account.number_display,
      label: account.label,
    };

    try {
      const accountResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          updateItem('payment_accounts', account!.id!, data),
        ),
      );

      await this.getBusinessPaymentAccounts(false);
      this.loading.set(false);
      return accountResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async softDeleteBusinessPaymentAccount(account: PaymentAccount) {
    this.loading.set(true);

    const data = {
      is_active: false,
    };

    try {
      const accountResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          updateItem('payment_accounts', account!.id!, data),
        ),
      );

      await this.getBusinessPaymentAccounts(false);
      this.loading.set(false);
      return accountResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }
}
